'use client';

import { animated, to } from '@react-spring/web';
import { clsx } from 'clsx';
import React, { useMemo, useState } from 'react';
import { useMount } from 'react-use';
import Image, { FastlyImageOptimizerOptions } from '~/components/Image';
import useSlider from '~/hooks/slider/useSlider';
import useZoom from '~/hooks/slider/useZoom';
import { RailsImage } from '~/typings/services/rails/item';

// What on earth...
const AnimatedDiv = animated('div') as React.ForwardRefExoticComponent<
  React.ComponentPropsWithRef<React.ElementType<{}, 'div'>>
>;

const FASTLY_PARAMS: FastlyImageOptimizerOptions = {
  height: 600,
  optimize: 'high',
};

const AnimatedSlide = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ children, className, ...props }, ref) => (
  <AnimatedDiv
    className={clsx('block h-[375px] w-screen', className)}
    {...props}
    ref={ref}
  >
    {children}
  </AnimatedDiv>
));

const AnimatedOverlay = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ children, className, ...props }, ref) => (
  <AnimatedDiv
    className={clsx('fixed inset-0', className)}
    {...props}
    ref={ref}
  >
    {children}
  </AnimatedDiv>
));

const AnimatedSlider = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ children, className, style, ...props }, ref) => (
  <AnimatedDiv
    className={clsx('grid w-full touch-pan-y select-none', className)}
    {...props}
    style={{
      gridAutoFlow: 'column',
      // @ts-expect-error
      WebkitUserDrag: 'none',
      WebkitTapHighlightColor: 'rgba(0, 0, 0, 0)',
      ...style,
    }}
    ref={ref}
  >
    {children}
  </AnimatedDiv>
));

interface Props {
  alt: string;
  images: RailsImage[];
}

const Slide: React.FC<
  React.PropsWithChildren<{ onScale: (...a: any[]) => any }>
> = ({ children, onScale }) => {
  const [element, scale, translateX, translateY, middleTouchOnElement] =
    useZoom({
      maxScale: 4,
      minScale: 1,
      onScale,
    });

  return (
    <AnimatedSlide
      ref={element}
      style={{
        // @ts-expect-error
        transform:
          scale && translateX && translateY
            ? to(
                [scale, translateX, translateY],
                (sc, x, y) =>
                  `translate3d(${x}px, ${y}px, 0) scale3d(${sc}, ${sc}, 1)`,
              )
            : undefined,
        transformOrigin: middleTouchOnElement?.to((x, y) => `${x}px ${y}px 0`),
      }}
    >
      {children}
    </AnimatedSlide>
  );
};

const MobileCarousel: React.FC<Props> = ({ images, alt }) => {
  const slides = useMemo(
    () =>
      images.map((im, index) => (
        <Image
          key={im.id}
          alt={alt}
          className="block h-[375px] w-full select-none object-contain"
          useFastly={FASTLY_PARAMS}
          draggable="false"
          loading={index === 0 ? 'eager' : 'lazy'}
          src={im.small_url}
        />
      )),
    [images],
  );

  const [zooming, scale, currentSlide, bind, x, onScale] = useSlider({
    initialSlide: 0,
    slides,
  });

  return (
    <div
      className="relative overflow-hidden"
      style={{
        height: 375,
      }}
    >
      {zooming && (
        <AnimatedOverlay
          style={{
            backgroundColor: scale
              ?.interpolate({ range: [1, 2, 10], output: [0, 0.7, 0.7] })
              ?.interpolate(opacity => `rgba(0, 0, 0, ${opacity})`),
          }}
        />
      )}

      <AnimatedSlider
        {...bind()}
        style={{
          transform: x?.interpolate(slideX => `translateX(${slideX}px`),
        }}
      >
        {slides.map((slide, index) => (
          <Slide key={index} onScale={onScale}>
            {slide}
          </Slide>
        ))}
      </AnimatedSlider>

      <div className="absolute bottom-4 flex w-full justify-center">
        {slides.map((s, i) => (
          <div
            key={i}
            className={clsx(
              'mx-1 h-2 w-2 rounded-full bg-white shadow transition duration-300 ease-in-out',
              i === currentSlide
                ? 'scale-110 bg-green-500 opacity-100'
                : 'scale-75 bg-gray-200 opacity-75',
            )}
          />
        ))}
      </div>
    </div>
  );
};

const MobileCarouselMemoryLeakFix: React.FC<Props> = props => {
  const [carouselHydrated, setCarouselHydrated] = useState(false);

  useMount(() => {
    setCarouselHydrated(true);
  });

  if (!carouselHydrated) {
    return (
      <div>
        <Image
          alt={props.alt}
          key={props.images?.[0]?.id}
          className="block h-[375px] w-full select-none object-contain"
          draggable="false"
          loading="eager"
          useFastly={FASTLY_PARAMS}
          src={props.images?.[0]?.small_url}
        />
      </div>
    );
  }

  return <MobileCarousel {...props} />;
};

export default MobileCarouselMemoryLeakFix;
